import React, { useEffect, useRef, useMemo, useState } from 'react';
import { Box, useTheme, Typography, Chip } from '@mui/material';
import * as d3 from 'd3';
import type { NumberValue } from 'd3';
import { ClusterReturnSeries } from '../calculation';

// Define which metrics are "higher is better" vs "lower is better"
const METRIC_DEFINITIONS = {
    anova_f: { higherBetter: true, name: 'ANOVA F-Score' },
    kruskal_f: { higherBetter: true, name: 'Kruskal F-Score' },
    silhouette: { higherBetter: true, name: 'Silhouette Score' },
    wasserstein: { higherBetter: false, name: 'Wasserstein Distance' },
    fragmentation: { higherBetter: false, name: 'Fragmentation' },
    davies_bouldin: { higherBetter: false, name: 'Davies-Bouldin Index' },
    cluster_entropy: { higherBetter: false, name: 'Cluster Entropy' },
    max_target_diff: { higherBetter: true, name: 'Max Target Difference' },
    mi_target_return: { higherBetter: true, name: 'MI Target Return' },
    calinski_harabasz: { higherBetter: true, name: 'Calinski-Harabasz Index' }
} as const;

interface MetricDataPoint {
    timestamp: Date;
    value: number;
    originalValue: number;
}

interface ParsedDataPoint {
    timestamp: Date;
    price: number;
    [key: string]: any; // For metric values
}

interface MetricSeries {
    metricName: string;
    values: MetricDataPoint[];
    isHigherBetter: boolean;
    color: string;
}

interface MetricsLineChartProps {
    clusterReturnSeries: ClusterReturnSeries;
    selectedMetrics: string[];
    movingAverageWindow: number;
}

export const MetricsLineChart: React.FC<MetricsLineChartProps> = ({
    clusterReturnSeries,
    selectedMetrics,
    movingAverageWindow
}) => {
    const theme = useTheme();
    const svgRef = useRef<SVGSVGElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [width, setWidth] = useState(800);
    const [hoverLine, setHoverLine] = useState<string | null>(null);

    // Color scale for different metrics
    const colorScale = d3.scaleOrdinal()
        .domain(Object.keys(METRIC_DEFINITIONS))
        .range([
            '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
            '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9'
        ]);

    // Define line styles for each metric
    const getLineStyle = (index: number) => {
        const styles = [
            { strokeDasharray: 'none' },           // solid
            { strokeDasharray: '5,5' },            // dashed
            { strokeDasharray: '10,3,3,3' },       // dash-dot
            { strokeDasharray: '2,3' },            // dotted
            { strokeDasharray: '8,4,2,4' }         // dash-dot-dot
        ];
        return styles[index % styles.length];
    };

    // Responsive width
    useEffect(() => {
        if (!containerRef.current) return;
        const observer = new ResizeObserver(entries => {
            for (let entry of entries) {
                setWidth(entry.contentRect.width);
            }
        });
        observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, []);

    // Process and normalize data
    const { processedMetrics, parsedData, yLeftDomain, yRightDomain } = useMemo(() => {
        const { timeseriesData } = clusterReturnSeries;
        if (!timeseriesData.length) return { processedMetrics: [], parsedData: [], yLeftDomain: [0, 1], yRightDomain: [0, 1] };

        // Filter data points that have cluster metrics for cluster 0
        const validDataPoints = timeseriesData.filter(d =>
            d.clusterMetrics && d.clusterMetrics[0]
        );

        if (!validDataPoints.length) return { processedMetrics: [], parsedData: [], yLeftDomain: [0, 1], yRightDomain: [0, 1] };

        // Parse data with timestamps and prices
        const parsedDataPoints: ParsedDataPoint[] = validDataPoints.map(d => ({
            timestamp: new Date(d.timestamp),
            price: d.price,
            ...Object.fromEntries(
                selectedMetrics.map(metric => [metric, d.clusterMetrics[0][metric]])
            )
        }));

        // Sort by timestamp to ensure proper order for moving average
        parsedDataPoints.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

        // Calculate 14-day moving averages for each metric
        const movingAverageData: ParsedDataPoint[] = [];

        for (let i = 0; i < parsedDataPoints.length; i++) {
            const currentPoint = parsedDataPoints[i];
            const movingAveragePoint: ParsedDataPoint = {
                timestamp: currentPoint.timestamp,
                price: currentPoint.price
            };

            // For each selected metric, calculate the moving average
            selectedMetrics.forEach(metricName => {
                const windowStart = Math.max(0, i - movingAverageWindow + 1);
                const windowData = parsedDataPoints
                    .slice(windowStart, i + 1)
                    .map(d => d[metricName])
                    .filter(v => v !== undefined && !isNaN(v) && isFinite(v));

                if (windowData.length > 0) {
                    const average = windowData.reduce((sum, val) => sum + val, 0) / windowData.length;
                    movingAveragePoint[metricName] = average;
                } else {
                    movingAveragePoint[metricName] = currentPoint[metricName];
                }
            });

            movingAverageData.push(movingAveragePoint);
        }

        // Calculate price domain
        const prices = movingAverageData.map(d => d.price);
        const priceDomain = [Math.min(...prices), Math.max(...prices)];

        // Calculate min/max for each selected metric for normalization (using moving averages)
        const metricExtents = new Map<string, [number, number]>();

        selectedMetrics.forEach(metricName => {
            const values = movingAverageData
                .map(d => d[metricName])
                .filter(v => v !== undefined && !isNaN(v) && isFinite(v));

            if (values.length > 0) {
                metricExtents.set(metricName, [Math.min(...values), Math.max(...values)]);
            }
        });

        // Process each metric into normalized series
        const processedMetrics = selectedMetrics.map(metricName => {
            const metricDef = METRIC_DEFINITIONS[metricName as keyof typeof METRIC_DEFINITIONS];
            const [min, max] = metricExtents.get(metricName) ?? [0, 1];

            const values: MetricDataPoint[] = movingAverageData
                .map(d => {
                    const originalValue = d[metricName];

                    // Skip invalid values
                    if (originalValue === undefined || isNaN(originalValue) || !isFinite(originalValue)) {
                        return null;
                    }

                    // Normalize to 0-1, but flip if lower is better
                    let normalizedValue;
                    if (max === min) {
                        normalizedValue = 0.5;
                    } else {
                        normalizedValue = (originalValue - min) / (max - min);
                        // Flip if lower values are better (so improvement always goes up)
                        if (!metricDef.higherBetter) {
                            normalizedValue = 1 - normalizedValue;
                        }
                    }

                    return {
                        timestamp: d.timestamp,
                        value: normalizedValue,
                        originalValue
                    };
                })
                .filter((point): point is MetricDataPoint => point !== null);

            return {
                metricName,
                values: values, // Already sorted
                isHigherBetter: metricDef.higherBetter,
                color: colorScale(metricName) as string
            };
        }).filter(metric => metric.values.length > 0);

        return {
            processedMetrics,
            parsedData: movingAverageData,
            yLeftDomain: priceDomain,
            yRightDomain: [0, 1]
        };
    }, [clusterReturnSeries, selectedMetrics, movingAverageWindow, colorScale]);

    // Calculate trend indicators
    const trendIndicators = useMemo(() => {
        return processedMetrics.map(metric => {
            if (metric.values.length < 2) return { metricName: metric.metricName, trend: 'stable' as const, change: 0 };

            const firstValue = metric.values[0].value;
            const lastValue = metric.values[metric.values.length - 1].value;
            const change = lastValue - firstValue;

            // Determine trend (improvement is always "up" due to normalization)
            let trend: 'improving' | 'deteriorating' | 'stable';
            if (Math.abs(change) < 0.05) trend = 'stable';
            else if (change > 0) trend = 'improving';
            else trend = 'deteriorating';

            return { metricName: metric.metricName, trend, change: Math.abs(change) };
        });
    }, [processedMetrics]);

    useEffect(() => {
        if (!svgRef.current || processedMetrics.length === 0 || parsedData.length === 0) return;

        const svg = d3.select(svgRef.current);
        svg.selectAll('*').remove();

        const margin = { top: 40, right: 50, bottom: 40, left: 60 };
        const height = 400 - margin.top - margin.bottom;
        const innerWidth = width - margin.left - margin.right;

        const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

        const allValues = processedMetrics.flatMap(d => d.values);
        const allTimestamps = [...parsedData.map(d => d.timestamp), ...allValues.map(d => d.timestamp)];

        const x = d3
            .scaleTime()
            .domain(d3.extent(allTimestamps) as [Date, Date])
            .range([0, innerWidth]);

        const yLeft = d3
            .scaleLinear()
            .domain(yLeftDomain)
            .range([height, 0]);

        const yRight = d3
            .scaleLinear()
            .domain(yRightDomain)
            .range([height, 0]);

        // Grid lines
        g.selectAll('.grid-line-horizontal')
            .data(yRight.ticks(5))
            .join('line')
            .attr('class', 'grid-line-horizontal')
            .attr('x1', 0)
            .attr('x2', innerWidth)
            .attr('y1', d => yRight(d))
            .attr('y2', d => yRight(d))
            .attr('stroke', theme.palette.divider)
            .attr('stroke-width', 0.5)
            .attr('stroke-dasharray', '2 2')
            .attr('stroke-opacity', 0.3);

        g.selectAll('.grid-line-vertical')
            .data(x.ticks(8))
            .join('line')
            .attr('class', 'grid-line-vertical')
            .attr('x1', d => x(d))
            .attr('x2', d => x(d))
            .attr('y1', 0)
            .attr('y2', height)
            .attr('stroke', theme.palette.divider)
            .attr('stroke-width', 0.5)
            .attr('stroke-dasharray', '2 2')
            .attr('stroke-opacity', 0.3);

        // Reference line at 0.5 (neutral) for metrics
        g.append('line')
            .attr('x1', 0)
            .attr('x2', innerWidth)
            .attr('y1', yRight(0.5))
            .attr('y2', yRight(0.5))
            .attr('stroke', theme.palette.text.secondary)
            .attr('stroke-width', 1)
            .attr('stroke-dasharray', '5 5')
            .attr('stroke-opacity', 0.5);

        // Price line
        const priceLine = d3
            .line<ParsedDataPoint>()
            .x(d => x(d.timestamp))
            .y(d => yLeft(d.price))
            .curve(d3.curveMonotoneX);

        g.append('path')
            .datum(parsedData)
            .attr('class', 'price-line')
            .attr('fill', 'none')
            .attr('stroke', theme.palette.text.primary)
            .attr('stroke-width', 2)
            .attr('stroke-opacity', hoverLine && hoverLine !== 'price' ? 0.1 : 1)
            .attr('d', priceLine)
            .on('mouseover', function () {
                svg.selectAll('.metric-line')
                    .attr('opacity', 0.1);
                svg.selectAll('.price-line').attr('opacity', 1);
                setHoverLine('price');
            })
            .on('mouseout', function () {
                svg.selectAll('.metric-line, .price-line')
                    .attr('opacity', 1);
                setHoverLine(null);
            });

        // Price hover area
        g.append('path')
            .datum(parsedData)
            .attr('class', 'price-hover')
            .attr('fill', 'none')
            .attr('stroke', 'transparent')
            .attr('stroke-width', 8)
            .attr('d', priceLine)
            .on('mouseover', () => setHoverLine('price'))
            .on('mouseout', () => setHoverLine(null));

        const line = d3
            .line<MetricDataPoint>()
            .x(d => x(d.timestamp))
            .y(d => yRight(d.value))
            .curve(d3.curveMonotoneX);

        // Draw metric lines
        g.selectAll('.metric-line')
            .data(processedMetrics)
            .join('path')
            .attr('class', 'metric-line')
            .attr('fill', 'none')
            .attr('stroke', d => d.color)
            .attr('stroke-width', 2)
            .attr('stroke-dasharray', (d, i) => getLineStyle(i).strokeDasharray)
            .attr('stroke-opacity', d =>
                hoverLine && hoverLine !== d.metricName ? 0.1 : 0.8
            )
            .attr('d', d => line(d.values) || '')
            .on('mouseover', function (event, d) {
                svg.selectAll('.metric-line, .price-line')
                    .attr('opacity', 0.1);
                d3.select(this).attr('opacity', 1);
                setHoverLine(d.metricName);
            })
            .on('mouseout', function () {
                svg.selectAll('.metric-line, .price-line')
                    .attr('opacity', 1);
                setHoverLine(null);
            });

        // Invisible thick lines for hovering
        g.selectAll('.hover-line')
            .data(processedMetrics)
            .join('path')
            .attr('class', 'hover-line')
            .attr('fill', 'none')
            .attr('stroke', 'transparent')
            .attr('stroke-width', 8)
            .attr('d', d => line(d.values) || '')
            .on('mouseover', (event, d) => setHoverLine(d.metricName))
            .on('mouseout', () => setHoverLine(null));

        /*
        // Metric labels on the right with trend indicators
        processedMetrics.forEach((metric, i) => {
            const lastPoint = metric.values[metric.values.length - 1];
            if (!lastPoint) return;
    
            const trendInfo = trendIndicators.find(t => t.metricName === metric.metricName);
            const trendIcon = trendInfo?.trend === 'improving' ? '↗' :
                trendInfo?.trend === 'deteriorating' ? '↘' : '→';
    
            g.append('text')
                .attr('x', innerWidth + 10)
                .attr('y', yRight(lastPoint.value) + 4)
                .attr('fill', metric.color)
                .attr('text-anchor', 'start')
                .style('font-size', '11px')
                .style('font-weight', hoverLine === metric.metricName ? 'bold' : 'normal')
                .text(`${trendIcon} ${METRIC_DEFINITIONS[metric.metricName as keyof typeof METRIC_DEFINITIONS].name}`);
        });
        */

        // Price label
        if (parsedData.length > 0) {
            const lastPoint = parsedData[parsedData.length - 1];
            g.append('text')
                .attr('x', innerWidth + 10)
                .attr('y', yLeft(lastPoint.price) + 4)
                .attr('fill', theme.palette.text.primary)
                .attr('text-anchor', 'start')
                .style('font-size', '11px')
                .style('font-weight', hoverLine === 'price' ? 'bold' : 'normal')
                .text('Price');
        }

        // Axes
        const tickDates = x.ticks(8);
        let lastMonth = -1;
        let lastYear = -1;

        g.append('g')
            .attr('transform', `translate(0,${height})`)
            .call(
                d3.axisBottom(x)
                    .tickValues(tickDates)
                    .tickFormat((domainValue: Date | NumberValue) => {
                        const date = new Date(domainValue as Date);
                        const month = date.getMonth();
                        const year = date.getFullYear();

                        let label: string;
                        if (lastYear === -1) {
                            label = d3.timeFormat('%d.%m.%Y')(date);
                        } else if (year !== lastYear) {
                            label = `${year}`;
                        } else if (month !== lastMonth) {
                            label = d3.timeFormat('%B')(date);
                        } else {
                            label = d3.timeFormat('%d.%m.%Y')(date);
                        }

                        lastMonth = month;
                        lastYear = year;
                        return label;
                    })
            );

        // Left axis (Price)
        g.append('g')
            .call(d3.axisLeft(yLeft).tickFormat(d => `$${(d as number).toFixed(0)}`));

        // Right axis (Metrics - normalized)
        g.append('g')
            .attr('transform', `translate(${innerWidth}, 0)`)
            .call(d3.axisRight(yRight).tickFormat(d => {
                if (d === 0.5) return 'Baseline';
                if (d === 0) return 'Worst';
                if (d === 1) return 'Best';
                return '';
            }));

        // Tooltip
        const tooltip = g.append('g')
            .attr('class', 'tooltip')
            .style('display', 'none');

        tooltip.append('rect')
            .attr('fill', 'rgba(0, 0, 0, 0.8)')
            .attr('rx', 4)
            .attr('ry', 4);

        const tooltipText = tooltip.append('text')
            .attr('fill', 'white')
            .attr('font-size', '12px');

        // Mouse interaction area
        g.append('rect')
            .attr('width', innerWidth)
            .attr('height', height)
            .attr('fill', 'transparent')
            .attr('pointer-events', 'all')
            .on('mousemove', function (event) {
                const [mouseX] = d3.pointer(event, this);
                const date = x.invert(mouseX);

                // Find closest data points
                const tooltipData: Array<{ metric: string, value: number, original: number, color: string, isPrice?: boolean }> = [];

                // Add price data
                if (parsedData.length > 0) {
                    let closestPricePoint = parsedData[0];
                    let minDistance = Math.abs(closestPricePoint.timestamp.getTime() - date.getTime());

                    parsedData.forEach(point => {
                        const distance = Math.abs(point.timestamp.getTime() - date.getTime());
                        if (distance < minDistance) {
                            minDistance = distance;
                            closestPricePoint = point;
                        }
                    });

                    tooltipData.push({
                        metric: 'Price',
                        value: closestPricePoint.price,
                        original: closestPricePoint.price,
                        color: theme.palette.text.primary,
                        isPrice: true
                    });
                }

                processedMetrics.forEach(metric => {
                    if (metric.values.length === 0) return;

                    let closestPoint = metric.values[0];
                    let minDistance = Math.abs(closestPoint.timestamp.getTime() - date.getTime());

                    metric.values.forEach(point => {
                        const distance = Math.abs(point.timestamp.getTime() - date.getTime());
                        if (distance < minDistance) {
                            minDistance = distance;
                            closestPoint = point;
                        }
                    });

                    tooltipData.push({
                        metric: METRIC_DEFINITIONS[metric.metricName as keyof typeof METRIC_DEFINITIONS].name,
                        value: closestPoint.value,
                        original: closestPoint.originalValue,
                        color: metric.color
                    });
                });

                if (tooltipData.length === 0) return;

                // Show tooltip
                tooltipText.selectAll('tspan').remove();

                tooltipText.append('tspan')
                    .attr('x', 8)
                    .attr('y', 15)
                    .style('font-weight', 'bold')
                    .text(new Date(date).toLocaleDateString());

                tooltipData.forEach((item, i) => {
                    tooltipText.append('tspan')
                        .attr('x', 8)
                        .attr('y', 35 + i * 16)
                        .attr('fill', item.color)
                        .text(`${item.metric}: ${item.isPrice ? `$${item.original.toFixed(2)}` : item.original.toFixed(3)}`);
                });

                const bbox = (tooltipText.node() as SVGTextElement).getBBox();
                tooltip.select('rect')
                    .attr('width', bbox.width + 16)
                    .attr('height', bbox.height + 16);

                const tooltipX = Math.min(mouseX + 10, innerWidth - bbox.width - 16);
                const tooltipY = Math.max(10, Math.min(event.offsetY - bbox.height - 20, height - bbox.height - 16));

                tooltip
                    .attr('transform', `translate(${tooltipX}, ${tooltipY})`)
                    .style('display', null);
            })
            .on('mouseout', () => tooltip.style('display', 'none'));

    }, [processedMetrics, parsedData, trendIndicators, hoverLine, theme, width, yLeftDomain, yRightDomain]);

    if (processedMetrics.length === 0) {
        return (
            <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography color="text.secondary">
                    No cluster metrics data available for selected metrics
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            {/* Trend Summary */}
            <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {trendIndicators.map(trend => (
                    <Chip
                        key={trend.metricName}
                        label={`${METRIC_DEFINITIONS[trend.metricName as keyof typeof METRIC_DEFINITIONS].name}`}
                        size="small"
                        color={trend.trend === 'improving' ? 'success' :
                            trend.trend === 'deteriorating' ? 'error' : 'default'}
                        variant={trend.trend === 'stable' ? 'outlined' : 'filled'}
                    />
                ))}
            </Box>

            <Box ref={containerRef} sx={{ width: '100%', overflowX: 'hidden' }}>
                <svg
                    ref={svgRef}
                    width={width}
                    height={400}
                    style={{ background: 'transparent' }}
                />
            </Box>
        </Box>
    );
};